/* eslint-disable max-lines */
import { useCallback, useMemo, useState } from 'react'
import { ChangeSet, IConfigurePositionsData, PositionLookups } from 'src/generated-types/configure-positions'
import { DrawingWithGroups } from 'src/generated-types/position-drawing-group'
import { IPositionDrawing } from 'src/generated-types/position-drawing'

export interface ChangeTrackerActions {
  changePositionDrawing: (
    positionId: number,
    positionName: string,
    newDrawingId: number,
    newDrawingNumber: string,
    originalDrawingId?: number,
    originalGroupId?: number,
  ) => void

  assignPositionToGroup: (
    positionId: number,
    positionName: string,
    groupId: number | null,
    groupName: string | undefined,
    drawingId: number,
    drawingNumber: string,
    originalDrawingId?: number,
    originalGroupId?: number,
  ) => void

  createNewGroup: (
    name: string,
    drawingId: number,
    drawingNumber: string,
    positionIds: number[],
    positionNames: string[],
  ) => string

  changeGroupDrawing: (groupId: number, newDrawingId: number, originalDrawingId: number, positionIds: number[]) => void

  unassignPosition: (
    positionId: number,
    positionName: string,
    originalDrawingId?: number,
    originalGroupId?: number,
    originalGroupName?: string,
  ) => void

  removePositionChange: (positionId: number) => void
  removeGroupChange: (groupIdOrTempId: number | string) => void
  resetChanges: () => void
  hasChanges: boolean
  changeSet: ChangeSet
  buildBulkRequest: () => IConfigurePositionsData[]
  getUnassignedPositions: () => number[]
}

interface UsePositionChangeTrackerProps {
  drawingGroups?: DrawingWithGroups[]
  positionDrawings?: IPositionDrawing[]
}

export const usePositionChangeTracker = ({
  drawingGroups,
  positionDrawings,
}: UsePositionChangeTrackerProps): { changeTracker: ChangeTrackerActions; lookups: PositionLookups } => {
  const [changeSet, setChangeSet] = useState<ChangeSet>({
    positionChanges: new Map(),
    groupChanges: new Map(),
  })

  // Build lookup maps for quick access to original data
  const lookups: PositionLookups = useMemo(() => {
    const positionToGroup = new Map<number, number>()
    const positionToDrawing = new Map<number, number>()
    const groupToDrawing = new Map<number, number>()
    const drawingIdToNumber = new Map<number, string>()
    const groupIdToName = new Map<number, string>()

    // First, populate from all available position drawings
    positionDrawings?.forEach(drawing => {
      drawingIdToNumber.set(drawing.id, drawing.drawingNumber)
    })

    // Then add/override from drawing groups (which have positions assigned)
    drawingGroups?.forEach(drawing => {
      drawingIdToNumber.set(drawing.drawingId, drawing.drawingNumber)
      drawing.groups?.forEach(group => {
        groupToDrawing.set(group.id, drawing.drawingId)
        groupIdToName.set(group.id, group.name)
        group.positions?.forEach(pos => {
          positionToGroup.set(pos.id, group.id)
          positionToDrawing.set(pos.id, drawing.drawingId)
        })
      })
    })

    return { positionToGroup, positionToDrawing, groupToDrawing, drawingIdToNumber, groupIdToName }
  }, [drawingGroups, positionDrawings])

  const changePositionDrawing = useCallback(
    (
      positionId: number,
      positionName: string,
      newDrawingId: number,
      newDrawingNumber: string,
      originalDrawingId?: number,
      originalGroupId?: number,
    ) => {
      setChangeSet(prev => {
        const next = {
          positionChanges: new Map(prev.positionChanges),
          groupChanges: new Map(prev.groupChanges),
        }

        const existing = next.positionChanges.get(positionId)
        const origDrawingId =
          existing?.originalDrawingId ?? originalDrawingId ?? lookups.positionToDrawing.get(positionId)
        const origDrawingNumber =
          existing?.originalDrawingNumber ?? (origDrawingId ? lookups.drawingIdToNumber.get(origDrawingId) : undefined)
        const origGroupId = existing?.originalGroupId ?? originalGroupId ?? lookups.positionToGroup.get(positionId)
        next.positionChanges.set(positionId, {
          positionId,
          positionName,
          originalDrawingId: origDrawingId,
          originalDrawingNumber: origDrawingNumber,
          originalGroupId: origGroupId,
          originalGroupName:
            existing?.originalGroupName ?? (origGroupId ? lookups.groupIdToName.get(origGroupId) : undefined),
          newDrawingId,
          newDrawingNumber,
          newGroupId: null,
        })

        return next
      })
    },
    [lookups],
  )

  const assignPositionToGroup = useCallback(
    (
      positionId: number,
      positionName: string,
      groupId: number | null,
      groupName: string | undefined,
      drawingId: number,
      drawingNumber: string,
      originalDrawingId?: number,
      originalGroupId?: number,
    ) => {
      setChangeSet(prev => {
        const next = {
          positionChanges: new Map(prev.positionChanges),
          groupChanges: new Map(prev.groupChanges),
        }

        const existing = next.positionChanges.get(positionId)
        const origDrawingId =
          existing?.originalDrawingId ?? originalDrawingId ?? lookups.positionToDrawing.get(positionId)
        const origDrawingNumber =
          existing?.originalDrawingNumber ?? (origDrawingId ? lookups.drawingIdToNumber.get(origDrawingId) : undefined)
        const origGroupId = existing?.originalGroupId ?? originalGroupId ?? lookups.positionToGroup.get(positionId)
        next.positionChanges.set(positionId, {
          positionId,
          positionName,
          originalDrawingId: origDrawingId,
          originalDrawingNumber: origDrawingNumber,
          originalGroupId: origGroupId,
          originalGroupName:
            existing?.originalGroupName ?? (origGroupId ? lookups.groupIdToName.get(origGroupId) : undefined),
          newDrawingId: drawingId,
          newDrawingNumber: drawingNumber,
          newGroupId: groupId,
          newGroupName: groupName,
        })

        return next
      })
    },
    [lookups],
  )

  const createNewGroup = useCallback(
    (
      name: string,
      drawingId: number,
      drawingNumber: string,
      positionIds: number[],
      positionNames: string[],
    ): string => {
      const tempId = `new-group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

      setChangeSet(prev => {
        const next = {
          positionChanges: new Map(prev.positionChanges),
          groupChanges: new Map(prev.groupChanges),
        }

        next.groupChanges.set(tempId, {
          tempId,
          name,
          drawingId,
          positionIds,
        })

        positionIds.forEach((positionId, index) => {
          const existing = next.positionChanges.get(positionId)
          const origDrawingId = existing?.originalDrawingId ?? lookups.positionToDrawing.get(positionId)
          const origDrawingNumber =
            existing?.originalDrawingNumber ??
            (origDrawingId ? lookups.drawingIdToNumber.get(origDrawingId) : undefined)
          const origGroupId = existing?.originalGroupId ?? lookups.positionToGroup.get(positionId)
          next.positionChanges.set(positionId, {
            positionId,
            positionName: positionNames[index] || `Position ${positionId}`,
            originalDrawingId: origDrawingId,
            originalDrawingNumber: origDrawingNumber,
            originalGroupId: origGroupId,
            originalGroupName:
              existing?.originalGroupName ?? (origGroupId ? lookups.groupIdToName.get(origGroupId) : undefined),
            newDrawingId: drawingId,
            newDrawingNumber: drawingNumber,
            newGroupId: null,
            newGroupName: name,
          })
        })

        return next
      })

      return tempId
    },
    [lookups],
  )

  const changeGroupDrawing = useCallback(
    (groupId: number, newDrawingId: number, originalDrawingId: number, positionIds: number[]) => {
      setChangeSet(prev => {
        const next = {
          positionChanges: new Map(prev.positionChanges),
          groupChanges: new Map(prev.groupChanges),
        }

        let groupName = ''
        drawingGroups?.forEach(drawing => {
          drawing.groups?.forEach(group => {
            if (group.id === groupId) {
              groupName = group.name
            }
          })
        })

        next.groupChanges.set(groupId, {
          groupId,
          name: groupName,
          drawingId: newDrawingId,
          originalDrawingId,
          positionIds,
        })

        positionIds.forEach(positionId => {
          const existing = next.positionChanges.get(positionId)
          const origDrawingId = existing?.originalDrawingId ?? originalDrawingId
          const origDrawingNumber =
            existing?.originalDrawingNumber ??
            (origDrawingId ? lookups.drawingIdToNumber.get(origDrawingId) : undefined)
          next.positionChanges.set(positionId, {
            positionId,
            positionName: existing?.positionName || `Position ${positionId}`,
            originalDrawingId: origDrawingId,
            originalDrawingNumber: origDrawingNumber,
            originalGroupId: existing?.originalGroupId ?? groupId,
            originalGroupName: existing?.originalGroupName ?? lookups.groupIdToName.get(groupId),
            newDrawingId,
            newGroupId: groupId,
          })
        })

        return next
      })
    },
    [drawingGroups, lookups],
  )

  const unassignPosition = useCallback(
    (
      positionId: number,
      positionName: string,
      originalDrawingId?: number,
      originalGroupId?: number,
      originalGroupName?: string,
    ) => {
      setChangeSet(prev => {
        const next = {
          positionChanges: new Map(prev.positionChanges),
          groupChanges: new Map(prev.groupChanges),
        }

        const existing = next.positionChanges.get(positionId)
        const origDrawingId =
          existing?.originalDrawingId ?? originalDrawingId ?? lookups.positionToDrawing.get(positionId)
        const origDrawingNumber =
          existing?.originalDrawingNumber ?? (origDrawingId ? lookups.drawingIdToNumber.get(origDrawingId) : undefined)
        const origGroupId = existing?.originalGroupId ?? originalGroupId ?? lookups.positionToGroup.get(positionId)
        next.positionChanges.set(positionId, {
          positionId,
          positionName,
          originalDrawingId: origDrawingId,
          originalDrawingNumber: origDrawingNumber,
          originalGroupId: origGroupId,
          originalGroupName:
            existing?.originalGroupName ??
            originalGroupName ??
            (origGroupId ? lookups.groupIdToName.get(origGroupId) : undefined),
          newDrawingId: originalGroupId ? origDrawingId : undefined, // Keep drawing assignment
          newDrawingNumber: originalGroupId ? origDrawingNumber : undefined, // Keep drawing number
          newGroupId: null,
        })

        return next
      })
    },
    [lookups],
  )

  const removePositionChange = useCallback((positionId: number) => {
    setChangeSet(prev => {
      const next = {
        positionChanges: new Map(prev.positionChanges),
        groupChanges: new Map(prev.groupChanges),
      }
      next.positionChanges.delete(positionId)
      return next
    })
  }, [])

  const removeGroupChange = useCallback((groupIdOrTempId: number | string) => {
    setChangeSet(prev => {
      const next = {
        positionChanges: new Map(prev.positionChanges),
        groupChanges: new Map(prev.groupChanges),
      }

      const groupChange = next.groupChanges.get(groupIdOrTempId)
      if (groupChange) {
        groupChange.positionIds.forEach(positionId => {
          next.positionChanges.delete(positionId)
        })
      }

      next.groupChanges.delete(groupIdOrTempId)
      return next
    })
  }, [])

  const resetChanges = useCallback(() => {
    setChangeSet({
      positionChanges: new Map(),
      groupChanges: new Map(),
    })
  }, [])

  const hasChanges = useMemo(() => {
    return changeSet.positionChanges.size > 0 || changeSet.groupChanges.size > 0
  }, [changeSet])

  // Get positions that need to be unassigned (drawing removed)
  const getUnassignedPositions = useCallback((): number[] => {
    const unassignedIds: number[] = []
    changeSet.positionChanges.forEach(change => {
      if (change.newDrawingId === undefined && change.newGroupId === null) {
        unassignedIds.push(change.positionId)
      }
    })
    return unassignedIds
  }, [changeSet])

  const buildBulkRequest = useCallback((): IConfigurePositionsData[] => {
    const requests: IConfigurePositionsData[] = []

    const groupedChanges = new Map<
      string,
      { positions: number[]; drawingId: number | null; groupId?: number | null; groupName?: string }
    >()

    changeSet.positionChanges.forEach(change => {
      // Handle unassigned positions (drawingId: null, groupId: null)
      const drawingId = change.newDrawingId ?? null
      const groupId = change.newGroupId ?? null

      // Use drawingId-groupName as the base key for grouping
      // This ensures positions going to the same group by name are grouped together
      const groupNameKey = change.newGroupName ?? ''
      const baseKey = `${drawingId}-${groupNameKey}`

      // Check if there's already an entry for this drawing+groupName combination
      let existingKey: string | null = null
      groupedChanges.forEach((value, key) => {
        if (key.startsWith(baseKey + '-')) {
          existingKey = key
        }
      })

      if (existingKey) {
        // Add to existing group, but update groupId if we have a valid one and existing doesn't
        const existing = groupedChanges.get(existingKey)!
        existing.positions.push(change.positionId)
        // Prefer valid groupId over null
        if (groupId && groupId > 0 && (!existing.groupId || existing.groupId <= 0)) {
          existing.groupId = groupId
        }
      } else {
        const key = `${baseKey}-${groupId}`
        groupedChanges.set(key, {
          positions: [change.positionId],
          drawingId: drawingId,
          groupId: groupId,
          groupName: change.newGroupName,
        })
      }
    })

    groupedChanges.forEach(group => {
      requests.push({
        positions: group.positions,
        drawingId: group.drawingId,
        groupId: group.groupId,
        groupName: group.groupName,
      })
    })

    return requests
  }, [changeSet])

  const changeTracker: ChangeTrackerActions = {
    changePositionDrawing,
    assignPositionToGroup,
    createNewGroup,
    changeGroupDrawing,
    unassignPosition,
    removePositionChange,
    removeGroupChange,
    resetChanges,
    hasChanges,
    changeSet,
    buildBulkRequest,
    getUnassignedPositions,
  }

  return { changeTracker, lookups }
}
